import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from './api';

/** Ответ любого списочного эндпоинта админки. */
export interface AdminListPage<T> {
  data: T[];
  total: number;
  nextCursor: string | null;
}

/**
 * Списки админки с догрузкой по курсору.
 *
 * Все четыре списка (юзеры, площадки, игры, клубы) просили `limit=100` и
 * молча выбрасывали `nextCursor`, который бэкенд возвращал с самого начала.
 * В итоге показывалось ровно 100 строк, а счётчик «Найдено» — настоящее
 * число: 186 юзеров и 4776 площадок было не достать вообще.
 *
 * Курсор, а не offset, потому что так устроены ручки: `(created_at, id) < …`.
 * Это заодно корректно при вставках между догрузками — страницы не съезжают.
 *
 * `params` с пустыми значениями отбрасываются, чтобы не слать `?q=&sort=`.
 */
export function useAdminList<T>(
  queryKey: readonly unknown[],
  path: string,
  params: Record<string, string | number | undefined> = {},
  pageSize = 100,
) {
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      qs.set('limit', String(pageSize));
      if (pageParam) qs.set('cursor', pageParam);
      return api.get<AdminListPage<T>>(`${path}?${qs.toString()}`);
    },
    // null от бэкенда = страниц больше нет; undefined выключает кнопку догрузки.
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];

  return {
    ...query,
    rows,
    /** Сколько всего подходит под фильтры — приходит с первой страницы. */
    total: query.data?.pages[0]?.total ?? 0,
    loaded: rows.length,
  };
}
